import { useContext, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  spreedlyCheckoutSdkCvvName,
  spreedlyCheckoutSdkIframeFields,
  spreedlyCheckoutSdkNumberName,
} from './spreedly-checkout-sdk-form.component';
import SpreedlyCheckoutSdkFormContext from './spreedly-checkout-sdk-form.context';
import { NewPaymentContext } from 'components/payment/payment.context';
import { useTheme } from '@mui/material';
import i18n from 'i18n';
import { useHandleOrderStatus } from 'pages/checkout/checkout-buttons-new/checkout-buttons-new.hooks';

// The auth (nonce/timestamp/signature) init-order hands us is only valid for
// a short, server-enforced window — not discoverable from the SDK bundle
// itself (it isn't a client-side constant). Rather than guess that window,
// this is handled reactively: recognize the server's own "timestamp expired"
// rejection and recover from it transparently instead of surfacing it.
const TIMESTAMP_EXPIRED_PATTERN = /timestamp/i;
const MAX_TIMESTAMP_EXPIRED_RETRIES = 1;
// If refetchInitOrder() fails outright (swallowed inside useInitOrderRequest's
// own error handler) or simply returns the same timestamp, auth?.timestamp
// never changes, the recreate-and-replay effect never re-runs, and 'ready'
// never fires again — silently stranding the swallowed submit/recache with
// nothing to tell the user. This bounds how long the auto-replay waits for
// that 'ready' before giving up and surfacing the original error instead.
const REPLAY_TIMEOUT_MS = 10000;

// The hosted field pages keep their own <body> transparent, but the <input>
// inside them falls back to the browser's default (white) background — set
// it explicitly so the card number/CVV fields blend into the form's own
// background like the surrounding MUI inputs do.
const inputStyle = {
  backgroundColor: 'transparent',
  width: '100%',
  height: '100%',
  fontSize: '16px',
  padding: '3px 0px',
};

export const useSpreedlyCheckoutSdkParameters = (initOrderAttributes = {}) => {
  const { nonce, timestamp, certificate_token, signature, SPREEDLY_ENV_KEY } =
    initOrderAttributes;

  return {
    environment_key: SPREEDLY_ENV_KEY,
    nonce,
    timestamp,
    certificate_token,
    signature,
  };
};

// The new SDK does its own client-side validation inside submit()/recache()
// and rejects locally with a plain string before ever reaching the server —
// unlike the legacy SDK, which always got a structured {attribute, key,
// message} 422 back. The docs (§8) only cite three exact strings ("Invalid
// card number", "Invalid CVV", "CVV is required for recaching"), but the
// shipped bundle has more ("Card number must be between 13 and 19 digits",
// etc.) — match on content instead of an exact-string allowlist so any of
// them route to their field the same way a fieldStateChange failure would.
const clientValidationAttribute = message => {
  const normalized = message.toLowerCase();
  if (normalized.includes('cvv')) return 'cvv';
  if (normalized.includes('card number')) return 'number';
  return undefined;
};

// The new SDK's `error` event hands us a plain string (client-side validation),
// the raw API error body (`{errors: [...]}`), a single {message, attribute} object,
// or nothing at all — normalize all of it to the array shape the rest of this file
// (and the reused SpreedlyGlobalErrors/SpreedlyCVVGlobalErrors display components) expect.
export const toSpreedlyCheckoutSdkErrorArray = payload => {
  if (typeof payload === 'string')
    return [
      {
        message: payload,
        attribute: clientValidationAttribute(payload),
        clientSide: true,
      },
    ];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.errors)) return payload.errors;
  if (payload) return [payload];
  return [];
};

// The server rejects tokenize/recache with this once the init-order auth is
// too old, regardless of attribute — surfaced as either a key or a message,
// so check both instead of relying on either shape alone.
export const isTimestampExpiredCheckoutSdkError = payload =>
  toSpreedlyCheckoutSdkErrorArray(payload).some(
    error =>
      TIMESTAMP_EXPIRED_PATTERN.test(error?.key ?? '') ||
      TIMESTAMP_EXPIRED_PATTERN.test(error?.message ?? ''),
  );

// The SDK itself clears the CVV field (and only the CVV field — the card
// number is untouched) three minutes after it's entered, for PCI DSS 3.2.3
// compliance, and reports it as "CVV expired after 3 minutes" — entirely
// unprompted, before the user ever submits. Both "cvv" and "expired" must
// appear together so this can't collide with an unrelated message like
// "Card has expired" (the card's own expiration date, a real validation
// error) or a plain "Invalid CVV".
export const isCvvExpiredCheckoutSdkError = payload =>
  toSpreedlyCheckoutSdkErrorArray(payload).some(error => {
    const text = `${error?.key ?? ''} ${error?.message ?? ''}`;
    return /cvv/i.test(text) && /expired/i.test(text);
  });

// Exported so callers that need to tell this specific synthesized error
// apart from other clientSide: true ones (e.g. a hosted native route
// deciding what's worth forwarding to the app) don't have to duplicate the
// literal.
export const SESSION_EXPIRED_ERROR_KEY = 'errors.session_expired';

const buildSessionExpiredError = () => [
  {
    key: SESSION_EXPIRED_ERROR_KEY,
    message: i18n.t('checkout.payment.spreedly.errors.sessionExpired'),
    clientSide: true,
  },
];

// Distinct from buildSessionExpiredError: a timestamp-expired auth needs the
// whole card re-entered because the entire instance gets destroyed and
// recreated, but the CVV TTL clear only ever empties the CVV field (docs
// above) — telling the user to redo their card number too would be wrong.
const buildCvvExpiredError = () => [
  {
    key: 'errors.cvv_expired',
    message: i18n.t('checkout.payment.spreedly.errors.cvvExpired'),
    clientSide: true,
  },
];

const setFieldValidationMessage = (field, valid) =>
  valid ? null : i18n.t(`checkout.payment.spreedly.errors.${field}`);

const applyCheckoutSdkStyles = (sdk, theme) => {
  const styleOverrides =
    theme.palette.type === 'dark' ? { color: theme.palette.text.primary } : {};

  sdk.setStyles('number', { ...inputStyle, ...styleOverrides });
  sdk.setStyles('cvv', { ...inputStyle, ...styleOverrides });
};

export const recacheCheckoutSdkPaymentInstrument = (sdk, paymentInstrument) => {
  sdk.setRecache(paymentInstrument.paymentToken, {
    card_type: paymentInstrument.cardType,
    last_four_digits: paymentInstrument.lastFour,
    // Recaching is only ever offered for a payment instrument we already saved,
    // so it is always retained — the new SDK fails closed without this (docs §6).
    storage_state: 'retained',
  });
};

const useHandleCheckoutSdkRecacheSuccess = () => {
  const handleOrderStatus = useHandleOrderStatus();

  return (sdk, callback) => {
    sdk.on('recacheSuccess', async () => {
      try {
        await callback?.();
      } catch (error) {
        handleOrderStatus(error);
      }
    });
  };
};

// A client-side validation string and our own synthesized errors (above)
// never left the browser — no request reached Spreedly, so nothing was
// consumed there and there's no reason to refetch the init-order. An actual
// `{errors: [...]}`/array/object payload, by contrast, is Spreedly's own
// response to a real submit()/recache() attempt — that attempt's nonce is
// spent either way, so the *next* attempt needs a fresh one. Used directly
// by the `error` dispatch in useSpreedlyCheckoutSdkForm below, which already
// owns every other refetchInitOrder() decision (timestamp-expired auto-retry,
// give-up) — display (calling `callback`) and session-reset (calling
// refetchInitOrder) are separate concerns handled in one place, rather than
// split between here and each caller's `onError`.
export const computeRequiresSessionReset = errors =>
  errors.length === 0 || errors.some(error => !error.clientSide);

export const handleCheckoutSdkErrors =
  (sdk, callback, setErrors, form) => payload => {
    const errors = toSpreedlyCheckoutSdkErrorArray(payload);
    callback(errors);
    // Indexing keeps every attribute-less error within *this* batch (see
    // below), but nothing else ever clears those slots — react-hook-form's
    // setError only ever adds/overwrites the exact paths it's given. Left
    // alone, a later, smaller batch (e.g. one synthesized CVV-expired error
    // after an earlier two-error server rejection) would only overwrite
    // global.0, leaving global.1 from the *previous* batch still rendering
    // underneath it. Clearing the whole namespace up front — even when this
    // batch turns out to have no attribute-less errors of its own — means
    // each call's display fully supersedes the last one's instead of merging
    // with it.
    form.clearErrors('global');
    errors.forEach((error, index) => {
      if (error.attribute === 'number' || error.attribute === 'cvv') {
        setErrors(prev => ({
          ...prev,
          [error.attribute]: error.message,
        }));
        sdk.transferFocus(error.attribute);
      } else if (!!error.attribute) {
        form.setError(error.attribute, {
          type: 'manual',
          message: error.message,
        });
      } else {
        // Keyed by the error's position in this batch, never by error.key
        // (e.g. the backend's own "errors.payment_method_not_found") —
        // react-hook-form treats a dot in a setError path as nesting, which
        // would bury the error under an extra level instead of the flat
        // object useSpreedlyGlobalErrors expects. key/message are carried as
        // plain data on the error object instead, not as part of the path.
        // Keying by index (rather than a single fixed path) also means a
        // batch with several attribute-less errors keeps all of them instead
        // of each setError call overwriting the last.
        form.setError(`global.${index}`, {
          type: 'global',
          key: error.key ?? 'errors',
          message: error.message,
        });
      }
    });
  };

// Unlike the legacy iframe SDK, submit()/recache() do their own client-side
// validation and reject locally with an `error` event when the number/CVV
// isn't valid (handleCheckoutSdkErrors above routes those to the same field
// display this drives from blur). So, unlike legacy's fieldEvent-driven
// isValidCVV flag, there's no need to gate submission on a client-tracked
// validity snapshot — doing so would race the iframe's async postMessage
// against how fast the caller submits, occasionally submitting a stale
// "invalid" reading. Submitting is always safe: the SDK itself is the
// authority on validity at the moment it actually tokenizes/recaches.
//
// fieldStateChange's own validNumber/validCvv are a *live*, lenient signal —
// e.g. a 1-digit CVV still reports validCvv: true while the user could still
// be typing. It is not a definitive "is this field complete and correct"
// check, so field-level error display can't be driven from it directly.
// validate() asks the SDK for that stricter, final answer, reported back on
// the separate `validation` event — same blur -> validate() -> validation
// round trip the legacy SDK needed, and for the same reason.
//
// validate() always reports both fields' validity together, but that
// doesn't mean both are ready to judge: tabbing out of the card number
// while CVV is still empty would otherwise show a premature "Invalid CVV"
// before the user has even reached it. So track which fields have been
// blurred so far and only update *those* fields' errors — falling back to
// updating both only when nothing has been blurred yet (e.g. an explicit
// validate() from a failed submit attempt with no field interaction at all).
//
// This deliberately tracks every blurred field rather than just the most
// recent one: the `validation` reply is an async postMessage round trip that
// doesn't say which validate() call it answers, so tabbing number -> cvv
// quickly can deliver the number blur's reply after the cvv blur has already
// happened. Keying off "last blurred" would attribute both replies to cvv and
// the number error would never show. Since every reply reports both fields'
// current validity, applying it to all touched fields is always correct.
export const handleCheckoutSdkValidation = (
  sdk,
  setErrors,
  paymentInstrumentToRecache,
) => {
  const blurredFields = new Set();
  sdk.on('fieldStateChange', state => {
    if (state.action === 'blur') {
      blurredFields.add(state.field);
      sdk.validate();
    }
  });

  sdk.on('validation', validation => {
    setErrors(prev => {
      if (blurredFields.size) {
        const next = { ...prev };
        if (blurredFields.has('number'))
          next.number = paymentInstrumentToRecache
            ? null
            : setFieldValidationMessage('number', validation.validNumber);
        if (blurredFields.has('cvv'))
          next.cvv = setFieldValidationMessage('cvv', validation.validCvv);
        return next;
      }
      return {
        ...prev,
        number: paymentInstrumentToRecache
          ? null
          : setFieldValidationMessage('number', validation.validNumber),
        cvv: setFieldValidationMessage('cvv', validation.validCvv),
      };
    });
  });
};

export const useSpreedlyCheckoutSdkForm = (
  initOrderAttributes,
  onSuccess,
  onError,
  paymentInstrumentToRecache,
) => {
  const auth = useSpreedlyCheckoutSdkParameters(initOrderAttributes);
  const handleRecacheSuccess = useHandleCheckoutSdkRecacheSuccess();
  const { refetchInitOrder } = useContext(NewPaymentContext);
  const [errors, setErrors] = useState({});
  const form = useFormContext();
  const theme = useTheme();
  const instanceRef = useRef(null);

  // Tracks the most recent submit/recache call so it can be transparently
  // replayed once a timestamp-expired auth is refreshed — the user shouldn't
  // have to click submit again just because the fresh instance needs a beat
  // to come up.
  const pendingActionRef = useRef(null);
  // Set right before the auth refetch that follows a timestamp-expired error,
  // and consumed by the next 'ready' event (the recreated instance) to know
  // it should replay pendingActionRef rather than sit idle.
  const replayOnReadyRef = useRef(false);
  // Backstop for replayOnReadyRef: cleared whenever 'ready' actually consumes
  // it (or the effect tears down for any reason) — if it instead fires, the
  // refetch never produced a usable instance in time.
  const replayTimeoutRef = useRef(null);
  // Guards against looping forever if the server keeps rejecting the same
  // action as expired even after a refresh (a real error at that point, not
  // just user delay) — capped per user-initiated attempt, reset below.
  const timestampRetryCountRef = useRef(0);
  // PAN and CVV are captured entirely inside Spreedly's own iframes (docs §5)
  // — our JS never sees those values, by design (that's what keeps this page
  // out of PCI scope). So a destroy-and-recreate to pick up a fresh auth also
  // silently empties whatever the user already typed, with no way for us to
  // read it back in. Tracks whether the *current* instance's fields have any
  // content, so a timestamp-expired recovery can tell whether it's safe to
  // auto-replay (nothing to lose) or must ask the user to re-enter instead.
  const fieldsHaveContentRef = useRef(false);

  const performSubmit = formData =>
    instanceRef.current?.submit({
      ...formData,
      month: formData.month.toString(),
      year: formData.year.toString(),
    });
  const performRecache = () => instanceRef.current?.recache();

  const replayPendingAction = () => {
    const pending = pendingActionRef.current;
    if (!pending) return;
    if (pending.type === 'submit') performSubmit(pending.formData);
    else performRecache();
  };

  // The underlying SDK instance is created (and destroyed/recreated) inside an
  // effect, well after mount, so submit/recache are exposed as stable
  // functions that read the latest instance lazily — callers get a working
  // reference to call whenever the user actually submits.
  const sdkActions = useRef({
    submit: formData => {
      timestampRetryCountRef.current = 0;
      pendingActionRef.current = { type: 'submit', formData };
      performSubmit(formData);
    },
    recache: () => {
      timestampRetryCountRef.current = 0;
      pendingActionRef.current = { type: 'recache' };
      performRecache();
    },
    // Only needed so the iframe fields report their own validity (as an
    // "error" event, docs §8) when the rest of the RHF form fails its own
    // validation and submit() never gets called otherwise — e.g. the user
    // never touched the card/CVV fields at all. Not part of a client-side
    // gate on submission itself; the SDK remains the sole authority there.
    validate: () => instanceRef.current?.validate(),
  }).current;

  useEffect(() => {
    const instance = new window.SpreedlyHostedFields(auth);
    instanceRef.current = instance;
    // A freshly constructed instance always mounts blank iframes, regardless
    // of what the previous (now-destroyed) instance's fields held.
    fieldsHaveContentRef.current = false;

    instance.inAppElements({
      number: { containerId: spreedlyCheckoutSdkNumberName },
      cvv: { containerId: spreedlyCheckoutSdkCvvName },
    });

    // `numberLength`/`cvvLength` are reported on every fieldStateChange event
    // regardless of which field changed (docs §9) — track them so an error
    // handler can tell, without ever seeing the actual PAN/CVV, whether the
    // fields are still empty.
    instance.on('fieldStateChange', state => {
      fieldsHaveContentRef.current =
        (state.numberLength ?? 0) > 0 || (state.cvvLength ?? 0) > 0;
    });

    instance.on('ready', () => {
      applyCheckoutSdkStyles(instance, theme);
      instance.setRequiredAttribute('number', true);
      instance.setRequiredAttribute('cvv', true);
      // A fresh instance has no armed recache state of its own, so this must
      // run again on every (re)creation — including one triggered by the
      // timestamp-expired recovery below — before a replayed recache() call
      // would have anything to act on.
      if (paymentInstrumentToRecache)
        recacheCheckoutSdkPaymentInstrument(
          instance,
          paymentInstrumentToRecache,
        );
      if (replayOnReadyRef.current) {
        replayOnReadyRef.current = false;
        replayPendingAction();
      }
    });

    if (paymentInstrumentToRecache) handleRecacheSuccess(instance, onSuccess);
    else
      instance.on('tokenGenerated', response => {
        const paymentMethod = response.tokenResponse.payment_method;
        onSuccess(paymentMethod.token, paymentMethod);
      });

    handleCheckoutSdkValidation(
      instance,
      setErrors,
      !!paymentInstrumentToRecache,
    );

    const handleError = handleCheckoutSdkErrors(
      instance,
      onError,
      setErrors,
      form,
    );
    instance.on('error', payload => {
      if (isCvvExpiredCheckoutSdkError(payload)) {
        // The SDK already cleared the CVV field itself (unprompted, before
        // any submit) — this is unrelated to our init-order auth, so there's
        // nothing to refresh and no instance to recreate here. Its own
        // message (not the auth-timestamp one) since only the CVV needs
        // re-entry — the card number iframe is untouched.
        handleError(buildCvvExpiredError());
        return;
      }
      if (isTimestampExpiredCheckoutSdkError(payload)) {
        if (fieldsHaveContentRef.current) {
          // The user already typed into the card/cvv iframes. We never have
          // access to those values ourselves — that isolation is the whole
          // point of Spreedly's hosted fields — and recreating the instance
          // to refresh auth wipes them with no way to put them back. Refresh
          // the auth anyway so the next attempt has valid credentials, but
          // ask the user to re-enter their details instead of blindly
          // replaying into fields that no longer hold what they typed.
          pendingActionRef.current = null;
          refetchInitOrder();
          handleError(buildSessionExpiredError());
          return;
        }
        if (timestampRetryCountRef.current < MAX_TIMESTAMP_EXPIRED_RETRIES) {
          // Nothing typed into the card/cvv iframes yet, so recreating the
          // instance to pick up a fresh auth loses nothing — swallow this
          // instead of routing to the normal display path, and replay the
          // pending action once the new instance is ready (its own
          // [auth?.timestamp] dependency handles the recreation), so the
          // user never sees this error at all.
          timestampRetryCountRef.current += 1;
          replayOnReadyRef.current = true;
          refetchInitOrder();
          // If refetchInitOrder() fails or happens to return the same
          // timestamp, auth?.timestamp never changes, this effect never
          // re-runs, and 'ready' never fires again — the swallowed submit
          // would otherwise vanish silently, with replayOnReadyRef left
          // armed for some later, unrelated instance recreation to misfire
          // on. Give up and surface the original error instead.
          replayTimeoutRef.current = setTimeout(() => {
            replayOnReadyRef.current = false;
            pendingActionRef.current = null;
            replayTimeoutRef.current = null;
            handleError(payload);
          }, REPLAY_TIMEOUT_MS);
          return;
        }
        // Retried with a fresh auth and still rejected as expired, with
        // nothing ever typed — a real problem at this point (e.g. clock
        // skew), not user delay. Give up rather than loop forever, and fall
        // through to the generic handling below — this is still a genuine
        // server rejection, so it refetches once more before surfacing the
        // original error rather than masking it.
      }
      // Whatever reaches here — a client-side validation string, our own
      // synthesized errors above, or a genuine `{errors: [...]}` rejection —
      // refetchInitOrder() is only warranted for the last of those (a real
      // request reached Spreedly and spent its nonce). This is the single
      // place that decides it, rather than leaving each `onError` caller to
      // re-derive the same thing from the error payload.
      if (computeRequiresSessionReset(toSpreedlyCheckoutSdkErrorArray(payload)))
        refetchInitOrder();
      handleError(payload);
    });

    return () => {
      // Whatever the reason this effect is tearing down — the recovery
      // succeeded and a new instance is about to take over, or the form is
      // unmounting entirely — a stale fallback belongs to an instance that
      // no longer exists.
      if (replayTimeoutRef.current) {
        clearTimeout(replayTimeoutRef.current);
        replayTimeoutRef.current = null;
      }
      instance.removeHandlers();
      instance.destroy();
      instanceRef.current = null;
    };
    // Auth is constructor-only and frozen after first use, so a change here
    // means destroy-and-recreate rather than an in-place re-init (docs §10.3).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.timestamp]);

  useEffect(() => {
    if (!instanceRef.current) return;
    applyCheckoutSdkStyles(instanceRef.current, theme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme.palette.text.primary, theme.palette.type]);

  return { errors, ...sdkActions };
};

export const useSpreedlyCheckoutSdkIframeField = name => {
  const { errors } = useContext(SpreedlyCheckoutSdkFormContext);
  const [error, setError] = useState(null);

  useEffect(() => {
    setError(errors[spreedlyCheckoutSdkIframeFields[name]]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  return { error };
};
